import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { BookOpen, ArrowRight, AlertCircle, MailCheck, RefreshCw, LogIn } from 'lucide-react';
import { loginWithEmail, registerWithEmail, logout, resendVerificationEmail } from '../services/firebase';
// @ts-ignore
import { getAuth, signInWithEmailAndPassword, signOut } from 'firebase/auth';

interface LoginProps {
  onLoginSuccess: (user: any) => void;
  logoSrc?: string;
  companyName?: string;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess, logoSrc, companyName }) => {
  const navigate = useNavigate();
  const [isRegistering, setIsRegistering] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Verification Screen State
  const [showVerification, setShowVerification] = useState(false);
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!email || !password) return;
    
    // Domain Restriction
    if (isRegistering && !email.toLowerCase().endsWith('@esselleretail.com')) {
        setError("Registration is restricted to @esselleretail.com email addresses.");
        return;
    }

    if (isRegistering && !name) {
        setError("Please enter your name.");
        return;
    }

    if (isRegistering && password !== confirmPassword) {
        setError("Passwords do not match.");
        return;
    }

    setLoading(true);
    setError(null);
    try {
      if (isRegistering) {
          // Register logic
          await registerWithEmail(email, password, name);
          // With strict auth, the user is signed out immediately.
          // We show the verification screen locally.
          setShowVerification(true);
      } else {
          // Login Logic
          const result: any = await loginWithEmail(email, password);
          onLoginSuccess(result);
      }
    } catch (err: any) {
      console.error(err);
      
      // Strict Auth Error Handling
      // Since we signed them out in the service, we can't get the user object easily.
      // But we have the email in the state.
      if (err.code === 'auth/email-not-verified') {
          setShowVerification(true);
      } else {
          setError(err.message || (isRegistering ? "Registration failed." : "Invalid credentials."));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    // Because we force logout unverified users, we must sign them in briefly to resend the email, then sign out again.
    if (!email || !password) {
        alert("Session expired. Please sign in again to resend verification email.");
        setShowVerification(false);
        return;
    }

    setResending(true);
    try {
        const auth = getAuth();
        // Silent Login
        const cred = await signInWithEmailAndPassword(auth, email, password);
        // Resend
        await resendVerificationEmail(cred.user);
        // Silent Logout
        await signOut(auth);
        
        alert(`Verification email resent to ${email}.`);
    } catch (e: any) {
        console.error(e);
        if (e.code === 'auth/too-many-requests') {
            alert("Please wait a few minutes before resending another email.");
        } else {
            alert(`Failed to resend email: ${e.message}`);
        }
    } finally {
        setResending(false);
    }
  };

  const toggleMode = () => {
      setIsRegistering(!isRegistering);
      setError(null);
      setConfirmPassword('');
      setPassword('');
      setName('');
      setShowVerification(false);
  };

  // If user registered or failed login due to verification
  if (showVerification) {
      return (
        <div className="min-h-screen bg-[#f8f7f5] flex flex-col items-center justify-center p-4">
            <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-md bg-white p-12 rounded-2xl shadow-xl border border-warm/20 text-center"
            >
                <div className="w-16 h-16 bg-yellow-50 text-yellow-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <MailCheck size={32} />
                </div>
                <h2 className="font-serif text-2xl text-dark mb-4">Verify Your Email</h2>
                <p className="text-cool mb-4 leading-relaxed">
                    We've sent a link to <strong className="text-dark">{email}</strong>.
                    <br/>You must click it to activate your account.
                </p>

                {/* Helpful tips for "Expired Link" issues */}
                <div className="bg-blue-50 text-blue-800 text-sm p-3 rounded-lg mb-4 text-left">
                    <strong>Seeing "Link Expired"?</strong><br/>
                    This often means your email provider (like Outlook) checked the link for you. 
                    If so, you are likely already verified! Try clicking "I've Verified My Email" below.
                </div>
                
                <div className="space-y-3">
                     {/* "I've Verified" attempts to log them in again with current password state */ }
                     <button 
                        onClick={() => handleSubmit()}
                        disabled={loading}
                        className="w-full py-3 bg-dark text-white rounded-lg font-semibold hover:bg-cool transition-colors shadow-lg shadow-dark/10 flex items-center justify-center gap-2"
                    >
                        {loading ? <RefreshCw className="animate-spin" size={18} /> : <LogIn size={18} />}
                        {loading ? 'Checking...' : "I've Verified My Email"}
                    </button>

                    <button 
                        onClick={handleResendEmail}
                        disabled={resending}
                        className="w-full py-3 bg-white border border-gray-300 text-dark rounded-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                    >
                        {resending ? <RefreshCw className="animate-spin" size={16} /> : <RefreshCw size={16} />}
                        {resending ? 'Sending...' : 'Resend Verification Email'}
                    </button>

                    <button 
                        onClick={() => { setShowVerification(false); }}
                        className="w-full py-3 text-cool hover:text-red-600 text-sm font-semibold transition-colors mt-2"
                    >
                        Back to Login
                    </button>
                </div>
            </motion.div>
        </div>
      );
  }

  return (
    <div className="min-h-screen bg-[#f8f7f5] flex flex-col items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white p-8 md:p-12 rounded-2xl shadow-xl border border-warm/20"
      >
        <div className="flex flex-col items-center mb-10 text-center">
          {logoSrc ? (
              <img src={logoSrc} alt={companyName || "Logo"} className="h-20 w-auto mb-4 object-contain" />
          ) : (
              <>
                <BookOpen className="h-10 w-10 text-dark mb-4" strokeWidth={1} />
                <h1 className="font-serif text-4xl text-dark tracking-tight">{companyName || "Esselle Retail"}</h1>
              </>
          )}
          
          <p className="text-[#8e8d8a] text-[9px] mt-4 tracking-[0.25em] uppercase font-semibold">Premium Publication Platform</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <AnimatePresence>
            {isRegistering && (
                <motion.div
                    initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                    animate={{ opacity: 1, height: 'auto', marginBottom: 20 }}
                    exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                    className="overflow-hidden"
                >
                    <label className="block text-xs font-semibold text-cool uppercase tracking-wider mb-2">Full Name</label>
                    <input 
                    type="text" 
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:border-dark focus:ring-1 focus:ring-dark outline-none transition-all font-sans"
                    placeholder="John Doe"
                    required={isRegistering}
                    />
                </motion.div>
            )}
          </AnimatePresence>

          <div>
            <label className="block text-xs font-semibold text-cool uppercase tracking-wider mb-2">Username / Email</label>
            <input 
              type="text" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:border-dark focus:ring-1 focus:ring-dark outline-none transition-all font-sans"
              placeholder={isRegistering ? "name@esselleretail.com" : "email@address.com"}
              required
              autoFocus={!isRegistering}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-cool uppercase tracking-wider mb-2">Password</label>
            <input 
              type="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:border-dark focus:ring-1 focus:ring-dark outline-none transition-all font-sans"
              placeholder="••••••••"
              required
            />
          </div>

          <AnimatePresence>
            {isRegistering && (
                <motion.div
                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                    animate={{ opacity: 1, height: 'auto', marginTop: 20 }}
                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                    className="overflow-hidden"
                >
                    <label className="block text-xs font-semibold text-cool uppercase tracking-wider mb-2">Confirm Password</label>
                    <input 
                    type="password" 
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:border-dark focus:ring-1 focus:ring-dark outline-none transition-all font-sans"
                    placeholder="••••••••"
                    required={isRegistering}
                    />
                </motion.div>
            )}
          </AnimatePresence>

          {error && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="flex items-start gap-2 text-red-600 bg-red-50 p-3 rounded-lg text-sm"
            >
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </motion.div>
          )}

          <button 
            type="submit" 
            disabled={loading}
            className="w-full py-4 bg-dark text-white rounded-lg font-semibold hover:bg-cool transition-colors flex items-center justify-center gap-2 group disabled:opacity-70 shadow-lg shadow-dark/10"
          >
            {loading ? (isRegistering ? 'Creating Account...' : 'Authenticating...') : (isRegistering ? 'Create Account' : 'Enter Dashboard')}
            {!loading && <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />}
          </button>
        </form>

        <div className="mt-6 flex flex-col gap-4 text-center">
             <button 
                onClick={toggleMode}
                className="text-sm text-cool hover:text-dark underline decoration-gray-300 underline-offset-4"
             >
                {isRegistering ? "Already have an account? Sign In" : "Don't have an account? Sign Up"}
             </button>
             <button 
                onClick={() => navigate('/')}
                className="text-sm text-cool hover:text-dark flex justify-center items-center gap-2"
             >
                <BookOpen size={16} /> Enter Public Library
             </button>
        </div>
      </motion.div>
      
      <p className="mt-8 text-cool/50 text-xs font-serif italic">
        The benchmark for digital booklets.
      </p>
    </div>
  );
};

export default Login;